import React from 'react';
import { CheckIcon, GraduationCapIcon, MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { MatchedSchool } from '../services/matchingApi';
import { normalizeSchool } from '../utils/schoolFields';
import { extractListItems } from '../utils/sanitizeHtml';

/** Partners send 1–8 bullets; cards must stay comparable in height. */
const MAX_BULLETS = 3;
interface MatchedSchoolCardProps {
  school: MatchedSchool;
  index: number;
}

/**
 * One API-sourced school in the results list.
 *
 * Mobile and desktop are laid out separately because they answer the question
 * differently. On desktop there is room for a rank column, the partner headline,
 * and a dedicated action column. On mobile the logo IS the identity — it is
 * given roughly a third of the card beside the selling points, the headline is
 * dropped, and the action spans the full width. The hidden layout is
 * display:none, so it is out of the tab order and the accessibility tree.
 */
export function MatchedSchoolCard({
  school,
  index
}: MatchedSchoolCardProps) {
  const normalized = normalizeSchool(school as Record<string, unknown>);
  const {
    name,
    logo,
    accreditation,
    headline,
    clickUrl,
    highlight
  } = normalized;
  const bullets = extractListItems(normalized.bodyHtml).slice(0, MAX_BULLETS);
  const position = ((school as Record<string, unknown>).mPosition || index + 1) as number;
  const isFirst = index === 0;
  const initials = (name || 'SC').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase();
  const rankCircle = <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isFirst ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-682" data-mp="1saenfo4eq22z">
      <span className="text-sm font-bold" data-id="element-683" data-mp="10ji8mhysdnd2">{position}</span>
      <span className="sr-only" data-id="element-684" data-mp="10gw1ukjpzbvw">Ranked number {position}</span>
    </div>;
  const highlightPill = highlight ? <span className="inline-flex items-center text-center bg-light border border-gray-200 text-navy text-[11px] sm:text-xs font-semibold leading-tight px-2.5 py-1 rounded-full" data-id="element-685" data-mp="27vho7cddirhn">
      {highlight}
    </span> : null;
  const logoBlock =
  // h-full matters: without it the image's max-h-full has no height to
  // resolve against and a tall wordmark overflows onto the school name.
  <div className="w-full h-full flex items-center justify-center" data-id="element-686" data-mp="2cokar0jqh0oc">
      {logo ? <img src={logo} alt={`${name} logo`} className="max-w-full max-h-full object-contain" loading="lazy" data-id="element-687" data-mp="1oypzgnf2bk67" /> : <div className="flex flex-col items-center gap-1 text-navy" data-id="element-688" data-mp="1xy38jez9y6qe">
          <GraduationCapIcon className="w-7 h-7" aria-hidden="true" data-id="element-689" data-mp="1tv6xc9c2dcwh" />
          <span className="text-sm font-bold" data-id="element-690" data-mp="1ty2sxr0fvphr">{initials}</span>
        </div>}
    </div>;
  const bulletList = bullets.length > 0 ? <ul className="space-y-2" data-id="element-691" data-mp="359tz43el891u">
        {bullets.map(bullet => <li key={bullet} className="flex items-start gap-2" data-id="element-692" data-mp="3cuo4lqp23yqv">
            <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-693" data-mp="3cwh9p1k0uawr" />
            <span className="text-[13px] sm:text-sm text-gray-700 leading-relaxed" data-id="element-694" data-mp="32wqcut7znjti">
              {bullet}
            </span>
          </li>)}
      </ul> : null;
  const accreditationLine = accreditation ? <p className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-gray-500" data-id="element-695" data-mp="3vlxbds7g09er">
      <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-696" data-mp="4eumj7f2uf7p" />
      {accreditation}
    </p> : null;
  const ctaButton = clickUrl ? <a href={clickUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center w-full min-h-[56px] lg:min-h-[60px] px-5 py-4 text-base font-bold rounded-xl text-white bg-cta shadow-md hover:bg-cta-dark hover:shadow-lg lg:hover:-translate-y-0.5 active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-dark transition-[background-color,box-shadow,transform] duration-200 ease-out" data-id="element-697" data-mp="3qtnsux8nqbmi">
      View Programs
    </a> : null;
  const onlineNote = <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-698" data-mp="2lq1x08kg7ert">
      <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-699" data-mp="2gqz2hi23gqy3" />
      100% Online
    </p>;
  return <article className="relative" data-id="element-700" data-mp="39da48w1fsxwh">
      {isFirst && <div className="rounded-t-xl px-4 py-2.5 sm:py-3 flex items-center justify-center gap-2 bg-navy" data-id="element-701" data-mp="39fw9uq4rp3c2">
          <TrophyIcon className="w-4 h-4 flex-shrink-0 text-gold-light" aria-hidden="true" data-id="element-702" data-mp="2znqgbbyvadur" />
          <span className="text-white text-xs sm:text-sm font-bold tracking-wide" data-id="element-703" data-mp="y02xq5dfwpr">
            #1 Recommended School
          </span>
        </div>}

      <div className={`bg-white transition-shadow duration-200 ease-out ${isFirst ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg hover:shadow-xl' : 'rounded-xl border border-gray-200 shadow-sm hover:shadow-md'}`} data-id="element-704" data-mp="3sabbknigra3l">
        {/* ---------- Mobile ---------- */}
        <div className="lg:hidden p-4" data-id="element-705" data-mp="ifykuljvw3ie">
          {/* Rank reads as "#1" rather than a bare circle — at this size a
              number on its own is ambiguous. */}
          <div className="flex items-center justify-between gap-3 pb-3" data-id="element-706" data-mp="ncpbg2tbehi2">
            <span className={`inline-block flex-shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold leading-none ${isFirst ? 'bg-[#14479E] border-[#14479E] text-white' : 'bg-[#F4F7FC] border-gray-200 text-[#14479E]'}`} data-id="element-707" data-mp="rekcd67c2svb">
              {`#${position}`}
              <span className="sr-only" data-id="element-708" data-mp="1b3kyynqqmqji"> ranked school</span>
            </span>
            {highlightPill}
          </div>

          {/* The logo carries identity on mobile, so it leads at full width —
              which also lets the bullets run the width of the card. */}
          <div className="h-20 flex items-center justify-center overflow-hidden" data-id="element-709" data-mp="gf1a84v9hu64">
            {logoBlock}
          </div>

          <div className="mt-4 text-center" data-id="element-710" data-mp="qeiechuv7zzz">
            <h3 className="text-lg font-bold text-navy leading-tight" data-id="element-711" data-mp="qdgz016tgrcg">
              {name}
            </h3>
            {accreditationLine && <div className="mt-1 flex justify-center" data-id="element-712" data-mp="z7lxn6s6z72j">
                {accreditationLine}
              </div>}
          </div>

          {bulletList && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-713" data-mp="1e2jqe0r95yk5">
              {bulletList}
            </div>}

          <div className="mt-4 space-y-2" data-id="element-714" data-mp="3fvptdgprpicl">
            {ctaButton}
            {onlineNote}
          </div>
        </div>

        {/* ---------- Desktop ---------- */}
        <div className="hidden lg:flex p-7 gap-8" data-id="element-715" data-mp="3ayze2vlseqs8">
          <div className="flex-1 min-w-0 flex gap-4" data-id="element-716" data-mp="1qeludp1ijp7k">
            {rankCircle}

            <div className="flex-1 min-w-0" data-id="element-717" data-mp="1vdp0zdx563hn">
              <h3 className="text-2xl font-bold text-navy leading-tight" data-id="element-718" data-mp="1vdfbdrg0dych">
                {name}
              </h3>
              {accreditationLine && <div className="mt-2" data-id="element-719" data-mp="1lnvo89l9jibt">{accreditationLine}</div>}
              {headline && <p className="mt-4 text-lg font-bold text-crimson leading-snug" data-id="element-720" data-mp="17nunqv8wucwv">
                  {headline}
                </p>}
              {bulletList && <div className="mt-4" data-id="element-721" data-mp="1bnmq2q7pnmts">{bulletList}</div>}
            </div>
          </div>

          <div className="w-60 flex-shrink-0 border-l border-gray-100 pl-8 flex flex-col items-center justify-center gap-4" data-id="element-722" data-mp="xs17p1vzg1ac">
            {highlightPill}
            <div className="w-full h-20 flex items-center justify-center" data-id="element-723" data-mp="2s3y2n4tmvpa5">
              {logoBlock}
            </div>
            {ctaButton}
            {onlineNote}
          </div>
        </div>
      </div>
    </article>;
}
export function DidYouKnowBanner() {
  return <p className="flex flex-wrap items-center justify-center gap-x-2 py-3 text-sm text-gray-600" data-id="element-724" data-mp="1fv49oykd292z">
      <span className="font-bold text-gray-800" data-id="element-725" data-mp="1b3vzw1f4clpa">DID YOU KNOW?</span>
      <span data-id="element-726" data-mp="1b2b282xicmn7">Online enrollments continue to grow year over year.</span>
    </p>;
}